/**
 * Product page add-ons ("pairs with").
 *
 * Drives the block rendered by snippets/pairs-with.liquid:
 *  - swipeable row of cards with pagination dots
 *  - size selection per card, pre-selected from the parent product's size
 *  - sale add-ons: POST /cart/add.js with the _pairs_with (and _addon_promo) properties
 *  - rental add-ons (Supercycle calendar): read the parent form's _cycle dates, check
 *    availability for those dates, match the rental period by length, create an intent,
 *    then add the line with the intent's attributes
 *  - resale add-ons (Supercycle resale): create a resale intent, then add
 *
 * Supercycle calls go through the app proxy (window.supercycleAppEmbed.context.proxyPathPrefix).
 * Docs: https://docs.supercycle.com/api-reference/storefront/intent
 *       https://docs.supercycle.com/api-reference/storefront/product-availability
 */
(() => {
  if (window.customElements.get('pairs-with')) return;

  const DAY_MS = 86400000;
  const ADDED_STATE_MS = 2500;

  const parseJSON = (str, fallback) => {
    try {
      return str ? JSON.parse(str) : fallback;
    } catch (e) {
      return fallback;
    }
  };

  // 'YYYY-MM-DD' (optionally with a time part) to a UTC midnight Date.
  const parseDate = (str) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(str || ''));
    return m ? new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))) : null;
  };

  const nightsBetween = (from, to) => Math.round((to - from) / DAY_MS);

  class PairsWith extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.strings = parseJSON(this.dataset.strings, {});
      // In the cart drawer the parent is a cart line: its _cycle arrives as data, not from a form.
      this.cartMode = this.dataset.context === 'cart';
      this.productForm = this.cartMode ? null : this.closest('product-form');
      this.form = this.cartMode ? null : (document.getElementById(this.dataset.formId)
        || (this.productForm && this.productForm.querySelector('.js-product-form')));
      this.mainSizeIndex = parseInt(this.dataset.mainSizeIndex, 10);
      this.mainSize = this.dataset.mainSize || '';
      this.rental = { raw: '', method: null, start: null, end: null, startStr: '', endStr: '', nights: null };
      this.availabilityToken = 0;

      this.cards = [...this.querySelectorAll('[data-pairs-with-item]')].map((el) => this.buildCard(el));

      this.initSlider();
      this.cards.forEach((card) => {
        this.applyMainSize(card);
        this.render(card);
      });

      this.clickHandler = this.handleClick.bind(this);
      this.addEventListener('click', this.clickHandler);

      if (this.cartMode) {
        this.applyCycle(this.dataset.cycle || '');
        return;
      }

      this.variantChangeHandler = this.handleVariantChange.bind(this);
      document.addEventListener('on:variant:change', this.variantChangeHandler);

      this.watchMainForm();
    }

    disconnectedCallback() {
      if (this.variantChangeHandler) document.removeEventListener('on:variant:change', this.variantChangeHandler);
      if (this.observer) this.observer.disconnect();
      clearInterval(this.pollTimer);
      clearTimeout(this.readTimer);
      if (this.formContainer) {
        this.formContainer.removeEventListener('change', this.readHandler);
        this.formContainer.removeEventListener('input', this.readHandler);
      }
      window.removeEventListener('on:debounced-resize', this.resizeHandler);
      this.initialised = false;
    }

    /**
     * Builds the state object for one add-on card.
     * @param {Element} el - The card element.
     * @returns {object}
     */
    buildCard(el) {
      const pills = [...el.querySelectorAll('.pairs-with-size')];
      const priceLabel = el.querySelector('[data-price-label]');
      const priceValue = el.querySelector('[data-price-value]');
      const card = {
        el,
        mode: el.dataset.mode, // rental | resale | sale, as rendered by Liquid
        activeMode: el.dataset.mode, // rental cards switch to resale while the parent is bought
        state: el.dataset.state,
        productId: Number(el.dataset.productId),
        handle: el.dataset.productHandle,
        title: el.dataset.productTitle || '',
        promo: el.dataset.promo || '',
        // Set only while offer badges are on: the label for the button tag and the reduced prices.
        promoLabel: el.dataset.promoLabel || '',
        promoPercent: Number(el.dataset.promoPercent || 0),
        rentFromPromo: el.dataset.rentFromPromo || '',
        resalePricePromo: el.dataset.resalePricePromo || '',
        salePricePromo: el.dataset.salePricePromo || '',
        periods: parseJSON(el.dataset.periods, []),
        rentFrom: el.dataset.rentFrom || '',
        resaleGlobalId: el.dataset.resaleGlobalId || '',
        resalePrice: el.dataset.resalePrice || '',
        initialLabel: priceLabel ? priceLabel.textContent.trim() : '',
        // The full price as Liquid rendered it; the element itself may carry the struck markup.
        initialValue: el.dataset.initialValue || (priceValue ? priceValue.textContent.trim() : ''),
        pills,
        variantId: el.dataset.variantId ? Number(el.dataset.variantId) : null,
        selectedVariantId: null,
        userPicked: false,
        availableVariantIds: null, // Set of variant ids free for the parent's dates (rental)
        checking: false,
        period: null,
        busy: false,
        button: el.querySelector('[data-add]'),
        priceLabel,
        priceValue,
        note: el.querySelector('[data-note]'),
      };
      if (!pills.length) card.selectedVariantId = card.variantId;
      return card;
    }

    cardFor(el) {
      const item = el.closest('[data-pairs-with-item]');
      return this.cards.find((card) => card.el === item) || null;
    }

    get proxyPrefix() {
      const embed = window.supercycleAppEmbed;
      return (embed && embed.context && embed.context.proxyPathPrefix) || '/apps/supercycle';
    }

    /* ---------------------------------------------------------------- Slider */

    initSlider() {
      this.slider = this.querySelector('.pairs-with__slider');
      this.carousel = this.querySelector('.pairs-with__carousel');
      this.items = [...this.querySelectorAll('.pairs-with__item')];
      this.dotsEl = this.querySelector('[data-dots]');
      if (!this.slider || !this.dotsEl) return;

      this.dotsEl.innerHTML = '';
      this.dots = this.items.map((item, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'pairs-with__dot';
        dot.setAttribute('aria-label', `${index + 1} / ${this.items.length}`);
        dot.addEventListener('click', () => this.scrollToIndex(index));
        this.dotsEl.appendChild(dot);
        return dot;
      });

      this.slider.addEventListener('scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = window.requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateDots();
        });
      }, { passive: true });

      this.resizeHandler = () => this.updateDots();
      window.addEventListener('on:debounced-resize', this.resizeHandler);
      this.updateDots();
    }

    /**
     * Updates the edge fades and the active dot from the current scroll position.
     * Cards snap to the centre of the row (first and last to their edge), so the active
     * card is the one whose centre is nearest the centre of the row.
     */
    updateDots() {
      if (!this.dots || !this.items.length) return;
      const { scrollLeft, clientWidth, scrollWidth } = this.slider;
      const overflows = scrollWidth > clientWidth + 1;
      const remaining = this.lastCardEnd() - (scrollLeft + clientWidth);
      const atEnd = remaining <= 1;

      if (this.carousel) {
        // Each fade thins out over the last fade width of travel instead of switching off.
        const fadeWidth = parseFloat(getComputedStyle(this.carousel).getPropertyValue('--fade-width')) || 48;
        const ease = (distance) => (overflows ? Math.min(Math.max(distance / fadeWidth, 0), 1) : 0);
        this.carousel.style.setProperty('--fade-start', ease(scrollLeft).toFixed(3));
        this.carousel.style.setProperty('--fade-end', ease(remaining).toFixed(3));
      }

      this.dotsEl.hidden = !overflows || this.items.length < 2;
      if (this.dotsEl.hidden) return;

      const origin = this.items[0].offsetLeft;
      let index = 0;
      if (atEnd) {
        index = this.items.length - 1;
      } else if (scrollLeft > 1) {
        const viewCentre = scrollLeft + clientWidth / 2;
        let best = Infinity;
        this.items.forEach((item, i) => {
          const itemCentre = item.offsetLeft - origin + item.offsetWidth / 2;
          const distance = Math.abs(itemCentre - viewCentre);
          if (distance < best) {
            best = distance;
            index = i;
          }
        });
      }
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
    }

    /**
     * Scrolls so the card rests where its snap position puts it: centred, or flush with
     * the edge for the first and last card.
     */
    scrollToIndex(index) {
      const item = this.items[index];
      if (!item) return;
      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
      const start = item.offsetLeft - this.items[0].offsetLeft;
      let left = start - (this.slider.clientWidth - item.offsetWidth) / 2;
      if (index === 0) left = 0;
      if (index === this.items.length - 1) left = this.lastCardEnd() - this.slider.clientWidth;
      this.slider.scrollTo({ left: Math.max(0, Math.min(left, maxScroll)), behavior: 'smooth' });
    }

    /**
     * Right edge of the last card in scroll coordinates. The row can hold trailing space
     * past the last card, so this, not scrollWidth, marks where the row really ends.
     */
    lastCardEnd() {
      const last = this.items[this.items.length - 1];
      return last.offsetLeft - this.items[0].offsetLeft + last.offsetWidth;
    }

    /* ----------------------------------------------------------------- Sizes */

    /**
     * Whether a size pill can be chosen in the card's current mode.
     */
    pillAvailable(card, pill) {
      if (card.activeMode === 'rental') {
        if (pill.dataset.rentable === 'false') return false;
        if (card.availableVariantIds) return card.availableVariantIds.has(Number(pill.dataset.variantId));
        return true;
      }
      return pill.dataset.inStock !== 'false';
    }

    selectPill(card, pill, byUser) {
      card.pills.forEach((p) => p.setAttribute('aria-pressed', p === pill ? 'true' : 'false'));
      card.selectedVariantId = pill ? Number(pill.dataset.variantId) : null;
      if (byUser) card.userPicked = true;
    }

    /**
     * Pre-selects the add-on size from the parent product's size: an exact match when it
     * is available, otherwise the nearest available size (the larger one on a tie).
     * A single-size add-on is selected outright. Never overrides a size the customer picked.
     */
    applyMainSize(card) {
      if (card.userPicked || !card.pills.length) return;
      let match = null;
      if (card.pills.length === 1) {
        [match] = card.pills;
      } else if (this.mainSize) {
        const wanted = this.mainSize.trim().toLowerCase();
        match = card.pills.find((p) => (p.dataset.size || '').trim().toLowerCase() === wanted) || null;
        if (!match || !this.pillAvailable(card, match)) match = this.nearestPill(card, this.mainSize);
      }
      if (match && !this.pillAvailable(card, match)) match = null;
      this.selectPill(card, match, false);
    }

    /**
     * The available size pill numerically nearest to the parent's size (EU 36 -> EU 38
     * before EU 34). Returns null when either side has no number to compare.
     */
    nearestPill(card, size) {
      const wanted = this.sizeNumber(size);
      if (wanted === null) return null;
      let best = null;
      let bestDistance = Infinity;
      card.pills.forEach((pill) => {
        if (!this.pillAvailable(card, pill)) return;
        const value = this.sizeNumber(pill.dataset.size);
        if (value === null) return;
        const distance = Math.abs(value - wanted);
        if (distance < bestDistance || (distance === bestDistance && best && value > this.sizeNumber(best.dataset.size))) {
          best = pill;
          bestDistance = distance;
        }
      });
      return best;
    }

    sizeNumber(size) {
      const m = /(\d+(?:[.,]\d+)?)/.exec(String(size || ''));
      return m ? parseFloat(m[1].replace(',', '.')) : null;
    }

    handleVariantChange(evt) {
      const detail = evt.detail || {};
      if (detail.form && this.form && detail.form !== this.form) return;
      const { variant } = detail;
      if (!variant || Number.isNaN(this.mainSizeIndex) || this.mainSizeIndex < 0) return;
      const options = variant.options || [variant.option1, variant.option2, variant.option3];
      const size = options[this.mainSizeIndex];
      if (typeof size !== 'string') return;
      this.mainSize = size;
      this.cards.forEach((card) => {
        this.applyMainSize(card);
        this.render(card);
      });
    }

    /* ----------------------------------------------------- Parent rental dates */

    /**
     * Watches the parent product form for the _cycle property the Supercycle Methods block
     * writes once dates are picked, and re-evaluates the rental add-ons when it changes.
     */
    watchMainForm() {
      this.formContainer = this.productForm || document.body;
      this.readHandler = () => this.readMainForm();
      const schedule = () => {
        clearTimeout(this.readTimer);
        this.readTimer = setTimeout(this.readHandler, 150);
      };
      this.observer = new MutationObserver(schedule);
      this.observer.observe(this.formContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value'],
      });
      this.formContainer.addEventListener('change', schedule);
      this.formContainer.addEventListener('input', schedule);
      // Input values set as properties are invisible to observers, so also poll cheaply.
      this.pollTimer = setInterval(this.readHandler, 1000);
      this.readMainForm();
    }

    readMainForm() {
      const scope = this.productForm || document;
      const input = scope.querySelector('[name="properties[_cycle]"]');
      this.applyCycle(input ? input.value : '');
    }

    /**
     * Takes a _cycle property (from the parent form, or from the parent cart line in the
     * drawer), reads the rental dates out of it and re-evaluates every card.
     * @param {string} raw - The _cycle JSON string, or '' when no dates are set.
     */
    applyCycle(raw) {
      if (raw === this.rental.raw) return;

      // The Methods block writes the rental dates as start.arrive_by / end.return_by;
      // rental_start / rental_end is the shape in the Supercycle docs, kept as a fallback.
      const cycle = parseJSON(raw, null) || {};
      const startRaw = (cycle.start && cycle.start.arrive_by) || cycle.rental_start;
      const endRaw = (cycle.end && cycle.end.return_by) || cycle.rental_end;
      const start = parseDate(startRaw);
      const end = parseDate(endRaw);
      this.rental = {
        raw,
        method: cycle.method_type || null,
        start,
        end,
        startStr: start ? String(startRaw).slice(0, 10) : '',
        endStr: end ? String(endRaw).slice(0, 10) : '',
        nights: start && end ? nightsBetween(start, end) : null,
      };
      this.availabilityToken += 1;
      this.cards.forEach((card) => {
        card.availableVariantIds = null;
        card.checking = false;
        this.setNote(card, '');
        card.activeMode = this.resolveMode(card);
        this.applyMainSize(card);
        this.render(card);
      });
      if (this.rental.start && this.rental.end) this.checkAvailability();
    }

    /**
     * One batched availability check for every rental add-on against the parent's dates.
     */
    async checkAvailability() {
      const rentalCards = this.cards.filter((card) => card.activeMode === 'rental');
      if (!rentalCards.length) return;
      const token = this.availabilityToken;
      rentalCards.forEach((card) => {
        card.checking = true;
        this.render(card);
      });
      try {
        const data = await this.supercycleFetch('/product_availability_checks', {
          product_ids: [...new Set(rentalCards.map((card) => card.productId))],
          rental_start: this.rental.startStr,
          rental_end: this.rental.endStr,
        });
        if (token !== this.availabilityToken) return;
        const byProduct = new Map();
        (data.products || []).forEach((product) => {
          byProduct.set(
            Number(product.shopifyId),
            new Set((product.availableVariants || []).map((variant) => Number(variant.shopifyId))),
          );
        });
        rentalCards.forEach((card) => {
          card.availableVariantIds = byProduct.get(card.productId) || new Set();
        });
      } catch (error) {
        if (token !== this.availabilityToken) return;
        // Unknown availability: leave sizes enabled, the intent is the final check at add time.
        console.error('pairs-with: availability check failed', error); // eslint-disable-line no-console
        rentalCards.forEach((card) => {
          card.availableVariantIds = null;
        });
      }
      if (token !== this.availabilityToken) return;
      rentalCards.forEach((card) => {
        card.checking = false;
        this.applyMainSize(card);
        this.render(card);
      });
    }

    periodNights(period) {
      if (typeof period.nights === 'number') return period.nights;
      if (typeof period.duration_count === 'number') {
        const unit = String(period.duration_unit || 'day').toLowerCase();
        if (unit.startsWith('week')) return period.duration_count * 7 - 1;
        return Math.max(period.duration_count - 1, 0);
      }
      const m = /(\d+)/.exec(period.name || '');
      return m ? Math.max(Number(m[1]) - 1, 0) : null;
    }

    /**
     * Finds the add-on rental period with the same length as the parent's rental.
     * Never reuses the parent's selling plan: every product has its own periods.
     */
    matchPeriod(card) {
      const { nights } = this.rental;
      if (nights === null) return null;
      const variantId = card.selectedVariantId;
      return card.periods.find((period) => {
        if (this.periodNights(period) !== nights) return false;
        if (!period.variant_application_type || period.variant_application_type === 'all') return true;
        if (variantId === null) return true;
        return Array.isArray(period.variant_ids) && period.variant_ids.map(Number).includes(variantId);
      }) || null;
    }

    /**
     * Wording for an add-on with no period of the parent's length: says which way the
     * add-on's periods lie, or falls back to the day count when they lie on both sides.
     */
    noPeriodText(card) {
      const s = this.strings;
      const { nights } = this.rental;
      const lengths = card.periods.map((period) => this.periodNights(period)).filter((n) => typeof n === 'number');
      if (lengths.length && lengths.every((n) => n > nights) && s.longer_rentals_only) return s.longer_rentals_only;
      if (lengths.length && lengths.every((n) => n < nights) && s.shorter_rentals_only) return s.shorter_rentals_only;
      return (s.not_for_duration || '').replace('[DAYS]', String(nights + 1));
    }

    formatDateRange() {
      const { start, end } = this.rental;
      const locale = document.documentElement.lang || 'en';
      const day = (date) => date.getUTCDate();
      const month = (date) => date.toLocaleDateString(locale, { month: 'short', timeZone: 'UTC' }).replace('.', '');
      if (!end) return `${day(start)} ${month(start)}`;
      if (start.getUTCMonth() === end.getUTCMonth() && start.getUTCFullYear() === end.getUTCFullYear()) {
        return `${day(start)}-${day(end)} ${month(end)}`;
      }
      return `${day(start)} ${month(start)}-${day(end)} ${month(end)}`;
    }

    scrollToDatePicker() {
      if (this.cartMode) return;
      const scope = this.productForm || document;
      const target = scope.querySelector('.supercycle-duration-picker-input__input')
        || scope.querySelector('.supercycle-methods');
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (typeof target.focus === 'function') {
        setTimeout(() => target.focus({ preventScroll: true }), 400);
      }
    }

    /* -------------------------------------------------------------- Rendering */

    resolveMode(card) {
      if (card.mode === 'rental' && this.rental.method === 'resale' && card.resaleGlobalId) return 'resale';
      return card.mode;
    }

    /**
     * Works out what a card should show from its mode, the parent's dates, availability and
     * the chosen size.
     */
    computeView(card) {
      const s = this.strings;
      const needsSize = card.pills.length > 0 && card.selectedVariantId === null;

      if (card.activeMode !== 'rental') {
        const resale = card.activeMode === 'resale' && card.resalePrice;
        return {
          state: 'ready',
          label: card.activeMode === 'resale' ? s.buy_for : card.initialLabel,
          value: resale ? card.resalePrice : card.initialValue,
          promoValue: resale ? card.resalePricePromo : card.salePricePromo,
          button: s.add,
          tag: card.promoLabel,
          disabled: needsSize,
        };
      }
      if (!this.rental.start || !this.rental.end) {
        return { state: 'needs-dates', label: s.rent_from, value: card.rentFrom, promoValue: card.rentFromPromo, button: s.choose_dates, disabled: true };
      }
      if (card.checking) {
        return { state: 'checking', label: s.checking, value: '', button: s.checking, disabled: true };
      }
      card.period = this.matchPeriod(card);
      if (!card.period) {
        const text = this.noPeriodText(card);
        return { state: 'no-period', label: text, value: '', button: text, disabled: true };
      }
      let anyAvailable = true;
      if (card.pills.length) {
        anyAvailable = card.pills.some((pill) => this.pillAvailable(card, pill));
      } else if (card.availableVariantIds) {
        anyAvailable = card.availableVariantIds.has(card.variantId);
      }
      if (!anyAvailable) {
        return { state: 'unavailable', label: s.none_for_dates, value: '', button: s.none_for_dates, disabled: true };
      }
      return {
        state: 'available',
        label: this.formatDateRange(),
        value: card.period.price,
        promoValue: card.period.promo_price || '',
        button: s.add,
        tag: card.promoLabel,
        disabled: needsSize,
      };
    }

    render(card) {
      if (card.state === 'adding' || card.state === 'added') return;
      card.activeMode = this.resolveMode(card);

      card.pills.forEach((pill) => {
        pill.disabled = !this.pillAvailable(card, pill);
      });
      if (card.selectedVariantId !== null && card.pills.length) {
        const selected = card.pills.find((pill) => Number(pill.dataset.variantId) === card.selectedVariantId);
        if (selected && selected.disabled) {
          // The chosen size stopped being free for the dates.
          this.selectPill(card, null, false);
          card.userPicked = false;
          if (card.activeMode === 'rental' && card.availableVariantIds) this.setNote(card, this.strings.size_dropped);
        }
      }

      this.applyView(card, this.computeView(card));
    }

    applyView(card, view) {
      card.state = view.state;
      card.el.dataset.state = view.state;
      card.el.dataset.activeMode = card.activeMode;
      if (card.priceLabel) card.priceLabel.textContent = view.label || '';
      if (card.priceValue) this.renderPrice(card.priceValue, view.value || '', view.promoValue || '');
      if (card.button) {
        this.renderButton(card.button, view.button || '', view.tag || '');
        if (view.disabled) card.button.setAttribute('aria-disabled', 'true');
        else card.button.removeAttribute('aria-disabled');
      }
    }

    /**
     * Writes a price, with the full price struck through when a promo gives a reduced one.
     */
    renderPrice(el, value, promoValue) {
      el.textContent = '';
      if (promoValue && value) {
        const was = document.createElement('s');
        was.className = 'pairs-with-card__was';
        was.textContent = value;
        el.append(was, promoValue);
      } else {
        el.textContent = value;
      }
    }

    /**
     * Writes the button label, with the offer tag beside it when a promo applies.
     */
    renderButton(button, text, tag) {
      button.textContent = '';
      if (tag) {
        const label = document.createElement('span');
        label.textContent = text;
        const badge = document.createElement('span');
        badge.className = 'pairs-with-card__btn-tag';
        badge.textContent = tag;
        button.append(label, badge);
      } else {
        button.textContent = text;
      }
    }

    setState(card, state, buttonText) {
      card.state = state;
      card.el.dataset.state = state;
      if (card.button && typeof buttonText === 'string') card.button.textContent = buttonText;
    }

    setNote(card, text) {
      if (!card.note) return;
      card.note.textContent = text || '';
      card.note.hidden = !text;
    }

    /* ------------------------------------------------------------ Interaction */

    handleClick(evt) {
      const pill = evt.target.closest('.pairs-with-size');
      if (pill && this.contains(pill)) {
        const card = this.cardFor(pill);
        if (!card || pill.disabled || card.busy) return;
        const alreadySelected = pill.getAttribute('aria-pressed') === 'true';
        this.selectPill(card, alreadySelected ? null : pill, true);
        this.setNote(card, '');
        this.render(card);
        return;
      }

      const button = evt.target.closest('[data-add]');
      if (button && this.contains(button)) {
        evt.preventDefault();
        const card = this.cardFor(button);
        if (card) this.handleAdd(card);
      }
    }

    async handleAdd(card) {
      if (card.busy || card.state === 'adding' || card.state === 'added') return;
      const s = this.strings;
      this.setNote(card, '');

      if (card.activeMode === 'rental') {
        if (!this.rental.start || !this.rental.end) {
          this.scrollToDatePicker();
          return;
        }
        if (card.state === 'checking' || card.state === 'unavailable' || card.state === 'no-period') return;
      }
      if (card.pills.length && card.selectedVariantId === null) {
        this.setNote(card, s.choose_size);
        return;
      }
      const variantId = card.selectedVariantId || card.variantId;
      if (!variantId) return;

      card.busy = true;
      this.setState(card, 'adding', s.adding);
      try {
        const properties = { _pairs_with: this.dataset.productHandle };
        if (card.promo) properties._addon_promo = card.promo;
        const line = { id: variantId, quantity: 1, properties };
        const items = [line];

        if (card.activeMode === 'rental' || card.activeMode === 'resale') {
          const option = card.activeMode === 'rental'
            ? { global_id: card.period.global_id, params: { rental_start: this.rental.startStr } }
            : { global_id: card.resaleGlobalId };
          const intent = await this.supercycleFetch('/intents', { variant_id: variantId, option });

          if (typeof intent.available_item_count === 'number' && intent.available_item_count === 0) {
            card.busy = false;
            card.state = '';
            if (card.activeMode === 'rental' && card.availableVariantIds) card.availableVariantIds.delete(variantId);
            this.render(card);
            if (card.state !== 'unavailable') {
              this.setNote(card, card.activeMode === 'resale' ? s.not_for_sale : s.none_for_dates);
            }
            return;
          }

          Object.entries(intent.attributes || {}).forEach(([key, value]) => {
            const match = /^properties\[(.+)\]$/.exec(key);
            if (match) line.properties[match[1]] = value;
            else line[key] = value;
          });
          if (card.activeMode === 'rental') {
            Object.entries(this.visibleRentalProperties()).forEach(([key, value]) => {
              if (!(key in line.properties)) line.properties[key] = value;
            });
          }
          items.push(...(await this.intentAddOns(intent.add_ons || [])));
        }

        const response = await this.addToCart(items);
        this.afterAdd(card, response);
        this.checkPromoLanded(card, response, variantId);
      } catch (error) {
        console.error('pairs-with: add failed', error); // eslint-disable-line no-console
        this.setNote(card, error && error.cartError && error.message ? error.message : s.error);
        card.state = '';
        this.render(card);
      } finally {
        card.busy = false;
      }
    }

    /**
     * A promo is only a property on the line; the price cut comes from the store's discount.
     * When the card quoted a reduced price and the added line carries no discount, say so,
     * so nobody is told a price the cart does not honour.
     */
    checkPromoLanded(card, response, variantId) {
      if (!card.promo || !card.promoLabel) return;
      const lines = Array.isArray(response && response.items) ? response.items : [response];
      const line = lines.find((item) => item && Number(item.variant_id) === Number(variantId));
      if (!line) return;
      const allocations = line.line_level_discount_allocations || [];
      const landed = allocations.length > 0 || Number(line.total_discount || 0) > 0;
      if (landed) return;
      console.error('pairs-with: promo did not apply to the cart line', card.promo); // eslint-disable-line no-console
      this.setNote(card, this.strings.promo_missing);
    }

    /**
     * The visible line properties the Methods block writes next to _cycle (Start date, End
     * date), so an add-on line shows the same dates in the cart as the parent product.
     */
    visibleRentalProperties() {
      if (this.cartMode) {
        const result = {};
        if (this.dataset.startLabel) result['Start date'] = this.dataset.startLabel;
        if (this.dataset.endLabel) result['End date'] = this.dataset.endLabel;
        return result;
      }
      const scope = this.form || this.productForm || document;
      const result = {};
      scope.querySelectorAll('input.supercycle-hidden-input[name^="properties["]').forEach((input) => {
        const match = /^properties\[(.+)\]$/.exec(input.name);
        if (match && !match[1].startsWith('_') && input.value) result[match[1]] = input.value;
      });
      return result;
    }

    /**
     * Extra lines an intent asks for (deposits, card authorisation), skipping any that
     * would push that variant past its cart_quantity limit.
     */
    async intentAddOns(addOns) {
      if (!addOns.length) return [];
      const cart = await fetch(`${window.Shopify.routes.root}cart.js`)
        .then((response) => response.json())
        .catch(() => ({ items: [] }));
      const quantityOf = (id) => (cart.items || [])
        .filter((item) => item.variant_id === Number(id))
        .reduce((total, item) => total + item.quantity, 0);
      return addOns
        .filter((addOn) => addOn.cart_quantity == null
          || quantityOf(addOn.id) + (addOn.quantity || 1) <= addOn.cart_quantity)
        .map(({ cart_quantity, ...rest }) => ({ quantity: 1, ...rest })); // eslint-disable-line camelcase
    }

    /**
     * Adds the lines and refreshes the cart drawer and header count, the way the theme's
     * product form does.
     */
    async addToCart(items) {
      const sectionsToUpdate = ['page-header', 'cart-drawer']
        .map((selector) => document.querySelector(selector))
        .filter((el) => el && el.dataset.sectionId && typeof el.updateFromCartChange === 'function');
      const routes = (window.theme && window.theme.routes) || {};
      const response = await fetch(routes.cartAdd || `${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items,
          sections: sectionsToUpdate.map((el) => el.dataset.sectionId).join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        const error = new Error(data.description || data.message || 'Could not add to cart');
        error.cartError = true;
        throw error;
      }
      if (data.sections && sectionsToUpdate.every((el) => data.sections[el.dataset.sectionId])) {
        sectionsToUpdate.forEach((el) => el.updateFromCartChange(data.sections[el.dataset.sectionId]));
      } else {
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      }
      return data;
    }

    afterAdd(card, data) {
      const settings = (window.theme && window.theme.settings) || {};
      const routes = (window.theme && window.theme.routes) || {};

      if (settings.afterAddToCart === 'page' && routes.cart) {
        setTimeout(() => {
          window.location.href = routes.cart;
        }, 300);
      } else if (settings.afterAddToCart === 'notification') {
        const template = document.getElementById('AddedNotification');
        if (template) {
          const notification = template.content.firstElementChild.cloneNode(true);
          notification.dataset.productTitle = (data && data.product_title) || card.title;
          (document.querySelector('.pageheader--sticky') || document.body).appendChild(notification);
        }
      } else {
        const drawer = document.querySelector('.js-cart-drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open(card.button);
      }

      this.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: { variantId: card.selectedVariantId || card.variantId, pairsWith: this.dataset.productHandle },
      }));

      this.setState(card, 'added', this.strings.added);
      clearTimeout(card.addedTimer);
      card.addedTimer = setTimeout(() => {
        if (card.state === 'added') {
          card.state = '';
          this.render(card);
        }
      }, ADDED_STATE_MS);
    }

    /* -------------------------------------------------------------- Supercycle */

    async supercycleFetch(path, body) {
      const response = await fetch(`${this.proxyPrefix}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Supercycle request failed (${response.status})`);
      return data;
    }
  }

  window.customElements.define('pairs-with', PairsWith);
})();
